import React from 'react';
import { Outlet, useNavigate, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Navbar } from '../Navbar';
import AppBootstrap from '@/providers/AppBootstrap';
import { useAuth } from '@/contexts/AuthProvider';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useUserMedia } from '@/hooks/useUserMedia';
import { useDefaultProfile } from '@/hooks/useDefaultProfile';
import { OffCanvasMenu, type OffCanvasMenuSection } from '@ui/index';
import { useIsAdmin } from '@/hooks/useRoleRequests';
import { getMediaBySlot } from '@/utils/mediaSlots';
import SeoHead from '@/components/SeoHead';
import { resolveVersionedSupabaseStorageDirectUrl } from '@/utils/supabaseStoragePublicUrl';
import { SEO_LOGO_URL } from '@/lib/seoConfig';
import { isProfileImageOwnedByUser } from '@/utils/profileImageOwnership';

export default function AppShell() {
  const { user, signOut } = useAuth();
  const { profile } = useUserProfile();
  const { media } = useUserMedia();
  const { getDefaultRoute, getDefaultProfileInfo } = useDefaultProfile();
  const defaultProfileInfo = getDefaultProfileInfo();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { data: isSuperAdmin } = useIsAdmin();
  const { t, i18n } = useTranslation();
  const isAndroid = React.useMemo(
    () => typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent),
    []
  );

  // Mobile Chrome guard: ensure we never leave the page scroll-locked.
  // Some overlays (menus/modals) can set body overflow hidden; make sure it's released on route changes.
  React.useEffect(() => {
    try { document.body.style.overflow = ""; } catch {}
    try { document.documentElement.style.overflow = ""; } catch {}
  }, [location.pathname]);

  React.useEffect(() => {
    if (menuOpen) return;
    try { document.body.style.overflow = ""; } catch {}
    try { document.documentElement.style.overflow = ""; } catch {}
  }, [menuOpen]);

  // Sync content top offset with real navbar height without forcing synchronous layout during boot.
  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const setNavOffset = (nextHeight?: number) => {
      const nav = document.querySelector('.nav-root') as HTMLElement | null;
      if (!nav) return;
      const measuredHeight =
        typeof nextHeight === 'number' && Number.isFinite(nextHeight)
          ? nextHeight
          : nav.offsetHeight;
      const h = Math.max(80, Math.round(measuredHeight));
      document.documentElement.style.setProperty('--app-navbar-offset', `${h}px`);
    };
    const handleNavOffsetChange = () => setNavOffset();

    setNavOffset();
    const raf = requestAnimationFrame(() => setNavOffset());
    const nav = document.querySelector('.nav-root') as HTMLElement | null;
    const ro =
      nav && typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver((entries) => {
            const entry = entries[0];
            if (!entry) return;
            setNavOffset(entry.contentRect.height);
          })
        : null;
    if (nav && ro) ro.observe(nav);
    window.addEventListener('resize', handleNavOffsetChange);
    window.addEventListener('orientationchange', handleNavOffsetChange);

    return () => {
      cancelAnimationFrame(raf);
      ro?.disconnect();
      window.removeEventListener('resize', handleNavOffsetChange);
      window.removeEventListener('orientationchange', handleNavOffsetChange);
    };
  }, []);

  // Obtener avatar con la misma lógica que UserProfileLive (priorizar p1)
  const profileImageVersion = profile?.updated_at ?? profile?.created_at ?? profile?.user_id ?? null;
  const avatarUrl = (() => {
    const safeMedia = media || [];
    if (profile?.avatar_url && isProfileImageOwnedByUser(profile.avatar_url, profile?.user_id)) {
      return resolveVersionedSupabaseStorageDirectUrl(profile.avatar_url, profileImageVersion, { defaultBucket: 'media' }) ?? profile.avatar_url;
    }
    const avatarSlot = getMediaBySlot(safeMedia as any, 'avatar');
    if (avatarSlot?.url && isProfileImageOwnedByUser(avatarSlot.url, profile?.user_id)) {
      return resolveVersionedSupabaseStorageDirectUrl(avatarSlot.url, profileImageVersion, { defaultBucket: 'media' }) ?? avatarSlot.url;
    }
    const p1 = getMediaBySlot(safeMedia as any, 'p1');
    if (p1?.url && isProfileImageOwnedByUser(p1.url, profile?.user_id)) {
      return resolveVersionedSupabaseStorageDirectUrl(p1.url, profileImageVersion, { defaultBucket: 'media' }) ?? p1.url;
    }
    return undefined;
  })();

  const [isLoggingOut, setIsLoggingOut] = React.useState(false);

  const handleLogout = React.useCallback(async () => {
    // ✅ Prevenir múltiples clicks
    if (isLoggingOut) return;
    
    try {
      setIsLoggingOut(true);
      setMenuOpen(false);
      
      // ✅ Cerrar sesión (actualiza el estado inmediatamente)
      await signOut();
      
      // ✅ Navegar inmediatamente a login
      navigate('/auth/login', { replace: true });
    } catch (error) {
      console.error('[AppShell] Error al cerrar sesión:', error);
      // ✅ Aún así navegar a login
      navigate('/auth/login', { replace: true });
    } finally {
      setIsLoggingOut(false);
    }
  }, [signOut, navigate, isLoggingOut]);

  const handleViewProfile = React.useCallback(() => {
    navigate(getDefaultRoute());
    setMenuOpen(false);
  }, [navigate, getDefaultRoute]);

  const menuSections = React.useMemo((): OffCanvasMenuSection[] => {
    const helpItems: OffCanvasMenuSection['items'] = [
      {
        id: 'roles-info',
        label: t('offcanvas_roles_prompt'),
        subtitle: t('offcanvas_sub_roles'),
        icon: '🎭',
        onClick: () => navigate('/app/roles/info'),
        activePath: '/app/roles/info',
      },
      {
        id: 'validation-info',
        label: t('validation_info'),
        subtitle: t('offcanvas_sub_validation'),
        icon: '✅',
        onClick: () => navigate('/validation/info'),
        activePath: '/validation/info',
      },
      {
        id: 'support',
        label: t('footer_support'),
        subtitle: t('offcanvas_sub_support'),
        icon: '🛟',
        onClick: () => navigate('/soporte'),
        activePath: '/soporte',
      },
      {
        id: 'legal',
        label: t('legal'),
        subtitle: t('offcanvas_sub_legal'),
        icon: '🔒',
        onClick: () => navigate('/aviso-de-privacidad'),
        activePath: '/aviso-de-privacidad',
      },
    ];

    const sections: OffCanvasMenuSection[] = [
      {
        id: 'main',
        title: t('offcanvas_section_main'),
        items: [
          {
            id: 'explore',
            label: t('home'),
            subtitle: t('offcanvas_sub_explore'),
            icon: '🏠',
            onClick: () => navigate('/explore'),
            activePath: '/explore',
            activeExact: true,
          },
        ],
      },
      {
        id: 'resources',
        title: t('offcanvas_section_help'),
        items: helpItems,
      },
    ];

    if (isSuperAdmin) {
      sections.push({
        id: 'admin',
        title: t('offcanvas_section_admin'),
        items: [
          {
            id: 'admin',
            label: t('admin'),
            icon: '🛡️',
            onClick: () => navigate('/admin/roles'),
            activePath: '/admin/roles',
          },
        ],
      });
    }

    return sections;
  }, [t, i18n.language, isSuperAdmin, navigate]);

  const logoutItem = React.useMemo(
    () => ({
      id: 'logout',
      label: t('logout'),
      icon: '🚪',
      onClick: handleLogout,
    }),
    [t, i18n.language, handleLogout]
  );

  return (
    <>
      <SeoHead section="default" />
      <style>{`
        .app-shell-root {
          min-height: 100vh;
          height: 100dvh;
          background: var(--db-bg, #160f14);
          color: var(--db-text, #faf7f5);
          display: flex;
          flex-direction: column;
          padding-top: 0;
          padding-bottom: 0;
          /* Asegurar que no haya overflow que cause problemas de stacking */
          overflow-x: hidden;
          overflow-y: hidden;
        }
        .app-shell-root.android-edge-to-nav {
          /* body ya puede traer safe-area; en Android esto crea hueco extra bajo navbar */
          margin-top: calc(-1 * max(env(safe-area-inset-top), 0px));
          padding-top: 0 !important;
        }
        .app-shell-content {
          flex: 1;
          min-height: 0;
          overflow-y: auto;
          overflow-x: hidden;
          -webkit-overflow-scrolling: touch;
          overscroll-behavior-y: contain;
          padding-top: var(--app-navbar-offset, 80px);
          padding-left: 1rem;
          padding-right: 1rem;
          padding-bottom: 0rem;
          isolation: isolate;
          z-index: 0;
        }
        @media (max-width: 768px) {
          .app-shell-content {
            padding-top: var(--app-navbar-offset, 80px);
            padding-bottom: 0.25rem;
            padding-left: 0.75rem;
            padding-right: 0.75rem;
            min-height: 0;
            touch-action: auto;
          }
        }
        @media (max-width: 480px) {
          .app-shell-content {
            padding-top: var(--app-navbar-offset, 80px);
            padding-bottom: .25rem;
            padding-left: 0.5rem;
            padding-right: 0.5rem;
          }
        }
        .app-shell-content.android-tight-top {
          padding-top: max(0px, calc(var(--app-navbar-offset, 80px) - 2px)) !important;
        }
        .app-screen-layout {
          --app-layout-safe-top: max(env(safe-area-inset-top, 0px), 0px);
          --app-layout-safe-bottom: max(env(safe-area-inset-bottom, 0px), 0px);
          --app-layout-safe-left: max(env(safe-area-inset-left, 0px), 0px);
          --app-layout-safe-right: max(env(safe-area-inset-right, 0px), 0px);
          --app-layout-navbar-offset: var(--app-navbar-offset, 80px);
          --app-layout-sticky-bottom-height: var(--app-sticky-bottom-height, 0px);
          --app-layout-bottom-space: calc(
            var(--app-layout-sticky-bottom-height) +
            var(--app-layout-safe-bottom) +
            16px
          );
          --app-overlay-background: rgba(19, 13, 20, 0.64);
          --app-overlay-backdrop-filter: blur(8px);
          min-height: 100%;
          width: 100%;
          min-width: 0;
        }
        .app-screen-layout--android {
          min-height: var(--app-visual-viewport-height, 100dvh);
          --app-overlay-background: rgba(19, 13, 20, 0.76);
          --app-overlay-backdrop-filter: none;
        }
        .app-shell-content:has(.app-screen-layout--edge-to-nav) {
          padding-top: 0 !important;
        }
        .app-screen-layout__content {
          width: 100%;
          min-width: 0;
        }
        .app-screen-layout__bottom-spacer {
          height: var(--app-layout-bottom-space);
          width: 100%;
          pointer-events: none;
          flex-shrink: 0;
        }
        .sticky-bottom-action {
          position: sticky;
          bottom: var(--app-layout-safe-bottom);
          z-index: 30;
          padding-bottom: var(--app-layout-safe-bottom);
        }
        .app-inline-loader {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          color: rgba(250, 247, 245, 0.82);
          line-height: 1.2;
        }
        .app-loader-dot {
          width: 8px;
          height: 8px;
          border-radius: 999px;
          background: currentColor;
          opacity: 0.75;
          animation: app-loader-pulse 0.9s ease-in-out infinite alternate;
        }
        .app-page-loader {
          min-height: var(--app-visual-viewport-height, 100dvh);
          display: grid;
          place-items: center;
          padding: var(--app-layout-navbar-offset) 16px var(--app-layout-bottom-space);
        }
        .app-overlay-loader {
          position: fixed;
          inset: 0;
          z-index: 10000;
          display: grid;
          place-items: center;
          padding: var(--app-layout-safe-top) var(--app-layout-safe-right) var(--app-layout-safe-bottom) var(--app-layout-safe-left);
          background: var(--app-overlay-background);
          backdrop-filter: var(--app-overlay-backdrop-filter);
          -webkit-backdrop-filter: var(--app-overlay-backdrop-filter);
          overscroll-behavior: contain;
        }
        @keyframes app-loader-pulse {
          from { transform: scale(0.8); opacity: 0.45; }
          to { transform: scale(1.2); opacity: 1; }
        }
        .app-screen-layout--debug-android,
        .app-screen-layout--debug-android * {
          outline: 1px solid rgba(0, 255, 255, 0.22);
          outline-offset: -1px;
        }
        .app-screen-layout--debug-android [style*="position: fixed"],
        .app-screen-layout--debug-android [style*="position:fixed"],
        .app-screen-layout--debug-android [style*="position: sticky"],
        .app-screen-layout--debug-android [style*="position:sticky"] {
          outline: 2px solid rgba(255, 138, 0, 0.9);
        }
        @media (max-width: 430px) {
          .app-shell-content {
            padding-bottom: 0.75rem !important;
          }
        }
        .app-footer {
          position: relative !important;
          flex-shrink: 0;
          z-index: 2;
          padding: 0.35rem 1.5rem;
          padding-bottom: calc(0.1rem + env(safe-area-inset-bottom));
          border-top: none;
          background: linear-gradient(180deg, rgba(26, 17, 23, 0.97) 0%, rgba(19, 13, 20, 0.99) 100%);
          backdrop-filter: blur(24px) saturate(1.2);
          -webkit-backdrop-filter: blur(24px) saturate(1.2);
          overflow: hidden;
          box-shadow: 0 -4px 24px rgba(19, 13, 20, 0.35), 0 -1px 0 rgba(250, 247, 245, 0.05) inset;
        }
        /* Escritorio: footer compacto y moderno */
        @media (min-width: 769px) {
          .app-footer {
            padding: 0.35rem 1.5rem;
            padding-bottom: calc(0.1rem + env(safe-area-inset-bottom));
          }
          .app-footer-content {
            gap: 1rem;
          }
          .app-footer-link {
            font-size: 0.8125rem;
            padding: 0.45rem 0.9rem;
          }
          .footer-separator {
            height: 22px;
          }
          .join-cta-button {
            display: none;
          }
        }
        @media (max-width: 768px) {
          .app-footer {
            padding-bottom: calc(0.1rem + env(safe-area-inset-bottom));
          }
        }
        @media (max-width: 480px) {
          .app-footer {
            padding: 0.35rem 0.5rem;
            padding-bottom: calc(0.1rem + env(safe-area-inset-bottom));
          }
        }
        .app-footer::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 2px;
          background: linear-gradient(90deg, 
            transparent 0%, 
            rgba(242, 90, 60, 0.45) 20%, 
            rgba(217, 154, 22, 0.72) 50%, 
            rgba(24, 168, 184, 0.45) 80%, 
            transparent 100%);
        }
        .app-footer-content {
          max-width: 1280px;
          width: 100%;
          margin: 0 auto;
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: nowrap;
          position: relative;
          z-index: 1;
          padding: 0;
        }
        
        .app-footer-content > * {
          flex-shrink: 0;
        }
        .app-footer-link {
          color: rgba(250, 247, 245, 0.8);
          text-decoration: none;
          font-size: 0.8125rem;
          font-weight: 600;
          padding: 0.5rem 1rem;
          border-radius: 999px;
          border: 1px solid rgba(255, 255, 255, 0.08);
          background: rgba(250, 247, 245, 0.045);
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          white-space: nowrap;
          line-height: 1;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        }
        .app-footer-link span:first-child {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          line-height: 1;
          vertical-align: middle;
          margin-top: -1px;
          flex: 0 0 auto;
        }
        
        .footer-link-text {
          display: inline;
        }
        .app-footer-link:hover {
          color: #fff;
          background: rgba(242, 90, 60, 0.16);
          border-color: rgba(242, 90, 60, 0.38);
          transform: translateY(-2px);
          text-decoration: none;
          box-shadow: 0 4px 16px rgba(242, 90, 60, 0.2);
        }
        .footer-separator {
          width: 1px;
          height: 20px;
          background: linear-gradient(180deg, 
            transparent 0%, 
            rgba(217, 154, 22, 0.42) 50%, 
            transparent 100%);
          opacity: 0.6;
          flex-shrink: 0;
        }
        .join-cta-button {
          display: none !important;
        }
        @media (max-width: 768px) {
          .app-footer {
            padding: 0.5rem 0.75rem;
          }
          .app-footer-content {
            gap: 0.6rem;
            flex-wrap: nowrap;
            justify-content: center;
            padding: 0;
          }
          .app-footer-link {
            font-size: 0.75rem;
            padding: 0.4rem 0.75rem;
            white-space: nowrap;
          }
          .app-footer-link span:last-child {
            display: inline;
          }
          .footer-separator {
            height: 18px;
            flex-shrink: 0;
          }
          .join-cta-button {
            display: none;
          }
        }
        @media (max-width: 480px) {
          .app-footer {
            padding: 0.4rem 0.5rem;
          }
          .app-footer-content {
            gap: 0.5rem;
            padding: 0;
          }
          .app-footer-link {
            font-size: 0.7rem;
            padding: 0.35rem 0.65rem;
            white-space: nowrap;
          }
          .app-footer-link span:last-child {
            display: inline;
          }
          .footer-separator {
            height: 16px;
            opacity: 0.5;
            flex-shrink: 0;
            width: 1px;
          }
          .join-cta-button {
            display: none;
          }
        }

        @media (max-width: 430px) {
          .app-footer {
            padding: 0.4rem !important;
            padding-bottom: calc(0.1rem + env(safe-area-inset-bottom)) !important;
          }
          .app-footer-content {
            gap: 0.35rem !important;
            flex-wrap: nowrap !important;
            justify-content: center !important;
            width: 100% !important;
            padding: 0 !important;
          }
          .app-footer-content > * {
            flex: 0 1 auto;
            min-width: 0;
            max-width: 100%;
          }
          .app-footer-link {
            font-size: 0.65rem !important;
            padding: 0.3rem 0.5rem !important;
            border-radius: 6px !important;
            text-align: center !important;
            white-space: nowrap !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
          }
          .app-footer-link span:first-child {
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            line-height: 1 !important;
            font-size: 0.7rem !important;
            flex-shrink: 0;
          }
          .app-footer-link .footer-link-text {
            display: inline !important;
            white-space: nowrap !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
            max-width: 120px;
          }
          .footer-separator {
            height: 14px !important;
            opacity: 0.15 !important;
            flex-shrink: 0 !important;
            width: 1px !important;
          }
          .join-cta-button {
            display: none !important;
          }
        }
        
        @media (max-width: 360px) {
          .app-footer {
            padding: 0.35rem 0.3rem !important;
            padding-bottom: calc(0.1rem + env(safe-area-inset-bottom)) !important;
          }
          .app-footer-content {
            gap: 0.25rem !important;
            padding: 0 !important;
          }
          .app-footer-link {
            font-size: 0.6rem !important;
            padding: 0.3rem 0.4rem !important;
          }
          .app-footer-link .footer-link-text {
            max-width: 100px !important;
          }
          .join-cta-button {
            display: none !important;
          }
        }
      `}</style>
      <div className={`app-shell-root${isAndroid ? ' android-edge-to-nav' : ''}`}>
        <Navbar onMenuToggle={user ? () => setMenuOpen(true) : undefined} isMenuOpen={menuOpen} />
        <div className={`app-shell-content${isAndroid ? ' android-tight-top' : ''}`}>
          <AppBootstrap>
            <Outlet />
          </AppBootstrap>
        </div>
        <footer className="app-footer">
          <div className="app-footer-content">
            <Link to="/soporte" className="app-footer-link">
              <span>🛟</span>
              <span className="footer-link-text">{t('footer_support')}</span>
            </Link>
            <div className="footer-separator" />
            <Link to="/aviso-de-privacidad" className="app-footer-link">
              <span>🔒</span>
              <span>{t('legal')}</span>
            </Link>
            <div className="footer-separator" />
            <Link to="/app/roles/info" className="app-footer-link">
              <span>🎭</span>
              <span className="footer-link-text">{t('roles_in_community_short')}</span>
            </Link>
          </div>
        </footer>

        {user && (
          <OffCanvasMenu
            isOpen={menuOpen}
            onClose={() => setMenuOpen(false)}
            pathname={location.pathname}
            menuSections={menuSections}
            logoutItem={logoutItem}
            userName={user.email?.split('@')[0] || t('user')}
            userEmail={user.email || ''}
            userAvatar={avatarUrl}
            displayName={profile?.display_name || undefined}
            profileBadge={defaultProfileInfo?.name}
            brandName={t('where_dance')}
            brandLogoUrl={SEO_LOGO_URL}
            menuTitle={t('offcanvas_menu_title')}
            closeLabel={t('close')}
            onViewProfile={handleViewProfile}
            viewProfileLabel={t('offcanvas_view_profile')}
            footerInfoLabel={t('offcanvas_footer_info')}
            footerLegalLabel={t('legal')}
            disabledItemHint={t('coming_soon')}
          />
        )}
      </div>
    </>
  );
}


